import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const Team = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const [expandedMember, setExpandedMember] = useState(null);

  const teamMembers = [
    {
      name: t.founder.name,
      role: t.team.roles.founder,
      visible: true,
      bio: null,
    },
    {
      name: 'Андрей Моисеев',
      role: `${t.team.roles.cofounder}, ${t.team.roles.communicationsDirector}`,
      visible: true,
      bio: t.team.bios?.andreyMoiseev,
    },
    {
      name: 'Даниил Шамраев',
      role: `${t.team.roles.cofounder}, ${t.team.roles.leadDeveloper}`,
      visible: true,
      bio: t.team.bios?.daniilShamraev,
    },
    {
      name: 'Кирилл Гоман',
      role: `${t.team.roles.cofounder}, ${t.team.roles.teamLead}`,
      visible: true,
      bio: t.team.bios?.kirillGoman,
    },
    {
      name: 'Альберт Хилажев',
      role: t.team.roles.marketingHead,
      visible: true,
      bio: t.team.bios?.albertKhilazhev,
    },
    {
      name: 'Li Wei (李伟)',
      role: t.team.roles.seniorDev,
      visible: false,
      bio: null,
    },
    {
      name: 'Carlos Mendoza',
      role: t.team.roles.securitySpecialist,
      visible: false,
      bio: null,
    },
    {
      name: 'Raj Patel',
      role: t.team.roles.aiEngineer,
      visible: false,
      bio: null,
    },
  ];

  return (
    <section
      id="team"
      data-testid="team-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 border-t border-zinc-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold/60 mb-8" data-testid="team-label">
            {t.team.label}
          </p>
          <h2 className="text-4xl md:text-6xl tracking-tight leading-none text-zinc-100 mb-8" data-testid="team-title">
            {t.team.title}
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-zinc-400 max-w-3xl">
            {t.team.description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.name}
              data-testid={`team-member-${index}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{
                duration: 1.2,
                delay: index * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group relative bg-[#121212] border border-zinc-900 hover:border-gold/50 transition-all duration-500 p-8"
            >
              <div className="space-y-4">
                <h3 className="text-xl md:text-2xl font-light text-zinc-100">{member.name}</h3>
                <p className="text-sm text-zinc-500 uppercase tracking-wider">{member.role}</p>
                
                {member.bio && (
                  <>
                    {expandedMember === index ? (
                      <div className="space-y-4">
                        <p className="text-sm leading-relaxed text-zinc-400">{member.bio}</p>
                        <button
                          onClick={() => setExpandedMember(null)}
                          data-testid={`team-collapse-${index}`}
                          className="text-xs text-gold hover:text-white transition-colors uppercase tracking-wider"
                        >
                          Свернуть
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setExpandedMember(index)}
                        data-testid={`team-expand-${index}`}
                        className="text-xs text-gold hover:text-white transition-colors uppercase tracking-wider"
                      >
                        Подробнее
                      </button>
                    )}
                  </>
                )}

                {!member.visible && (
                  <p className="text-xs text-zinc-600 italic mt-2">
                    {t.team.shadowNote}
                  </p>
                )}
              </div>

              {/* Gold accent line */}
              <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </motion.div>
          ))}
        </div>

        {/* Closing text */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1.2, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-24 pt-12 border-t border-zinc-900"
        >
          <p className="text-base md:text-lg leading-relaxed text-zinc-400 max-w-4xl">
            {t.team.closingText}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Team;
